import { useCallback } from 'react';

import { useTranslation } from 'react-i18next';

import {
  type TabBadge,
  type WorkflowTab,
  WorkflowTabBar,
} from '@/components/common';
import { IncubatorSelectionFields } from '@/pages/fert-work/components/IncubatorSelectionFields';
import { useIncubatorSelection } from '@/pages/fert-work/hooks/useIncubatorSelection';
import { getStoredIncubatorSelection } from '@/pages/fert-work/utils/incubatorSelection';

export type FertWorkTab = 'session' | 'runs' | 'labels' | 'maps' | 'sof-labels';

export type TabBarProps = {
  activeTab: FertWorkTab;
  onTabChange: (tab: FertWorkTab) => void;
  runCount: number;
  invalidRunCount: number;
  warningRunCount: number;
  labelCount: number;
  incubatorMapCount: number;
  sofLabelCount: number;
  sessionId: string;
  onGenerateRuns?: (matTimeWindowHours: number) => void;
  onGenerateMaps?: (
    incubatorCounts: Record<string, number>,
    preferenceOrder: string[],
  ) => void;
  isGeneratingRuns?: boolean;
  isGeneratingMaps?: boolean;
};

function RunsDropdown({ onSelect }: { onSelect: (hours: number) => void }) {
  const { t } = useTranslation();

  return (
    <div className="absolute top-full left-0 z-50 mt-1 w-44 rounded-md border border-gray-200 bg-white py-1 shadow-lg">
      <button
        type="button"
        className="w-full border-0 bg-transparent px-4 py-2 text-left text-sm text-gray-700 hover:bg-cyan-50 hover:text-cyan-700"
        onClick={(e) => {
          e.stopPropagation();
          onSelect(3);
        }}
      >
        {t('fertWork.hourWindow', '{hours} Hour Window', { hours: 3 })}
      </button>
      <button
        type="button"
        className="w-full border-0 bg-transparent px-4 py-2 text-left text-sm text-gray-700 hover:bg-cyan-50 hover:text-cyan-700"
        onClick={(e) => {
          e.stopPropagation();
          onSelect(2);
        }}
      >
        {t('fertWork.hourWindow', '{hours} Hour Window', { hours: 2 })}
      </button>
    </div>
  );
}

function MapsDropdown({
  onGenerate,
  labelCount,
  sessionId,
}: {
  onGenerate: (
    incubatorCounts: Record<string, number>,
    preferenceOrder: string[],
  ) => void;
  labelCount: number;
  sessionId: string;
}) {
  const { t } = useTranslation();
  const incubatorSelection = useIncubatorSelection(labelCount, sessionId);

  const handleGenerate = (e: React.MouseEvent) => {
    e.stopPropagation();
    const { incubatorCounts, preferenceOrder } =
      incubatorSelection.buildSelection();
    onGenerate(incubatorCounts, preferenceOrder);
  };

  return (
    <div className="absolute top-full left-0 z-50 mt-1 w-64 rounded-md border border-gray-200 bg-white p-3 shadow-lg">
      <IncubatorSelectionFields
        selection={incubatorSelection}
        labelCount={labelCount}
      />
      <button
        type="button"
        onClick={handleGenerate}
        disabled={
          incubatorSelection.activeTypeNames.length === 0 ||
          incubatorSelection.hasCapacityError
        }
        className={`w-full rounded border-0 px-3 py-1.5 text-sm font-medium ${
          incubatorSelection.activeTypeNames.length === 0 ||
          incubatorSelection.hasCapacityError
            ? 'cursor-not-allowed bg-gray-300 text-gray-500'
            : 'bg-cyan-600 text-white hover:bg-cyan-700'
        }`}
      >
        {t('fertWork.generate', 'Generate')}
      </button>
    </div>
  );
}

export const TabBar = ({
  activeTab,
  onTabChange,
  runCount,
  invalidRunCount,
  warningRunCount,
  labelCount,
  incubatorMapCount,
  sofLabelCount,
  sessionId,
  onGenerateRuns,
  onGenerateMaps,
  isGeneratingRuns = false,
  isGeneratingMaps = false,
}: TabBarProps) => {
  const { t } = useTranslation();

  const showRunsDropdown =
    runCount === 0 && !isGeneratingRuns && !!onGenerateRuns;
  const showMapsDropdown =
    incubatorMapCount === 0 &&
    !isGeneratingMaps &&
    !!onGenerateMaps &&
    labelCount > 0;

  const handleRunsSelect = useCallback(
    (hours: number) => {
      onGenerateRuns?.(hours);
      onTabChange('runs');
    },
    [onGenerateRuns, onTabChange],
  );

  const handleMapsGenerate = useCallback(
    (incubatorCounts: Record<string, number>, preferenceOrder: string[]) => {
      onGenerateMaps?.(incubatorCounts, preferenceOrder);
      onTabChange('maps');
    },
    [onGenerateMaps, onTabChange],
  );

  const getBadge = (tab: FertWorkTab): TabBadge | null => {
    let count = 0;
    let severity: TabBadge['severity'] = 'success';

    switch (tab) {
      case 'runs':
        count = runCount;
        if (invalidRunCount > 0) severity = 'danger';
        else if (warningRunCount > 0) severity = 'warning';
        break;
      case 'labels':
        count = labelCount;
        break;
      case 'maps':
        count = incubatorMapCount;
        break;
      case 'sof-labels':
        count = sofLabelCount;
        break;
      default:
        return null;
    }

    if (count === 0) return null;
    return { count, severity };
  };

  const isTabLocked = (tabKey: FertWorkTab) => {
    if (tabKey === 'labels') return runCount === 0;
    if (tabKey === 'maps') return labelCount === 0;
    if (tabKey === 'sof-labels') return incubatorMapCount === 0;
    return false;
  };

  const handleTabChange = (tab: FertWorkTab) => {
    if (tab === 'runs' && runCount === 0 && onGenerateRuns) {
      const saved = localStorage.getItem('fertwork-mat-time-window');
      const hours = saved ? parseInt(saved) : 3;
      onGenerateRuns(hours);
      onTabChange('runs');
      return;
    }
    if (tab === 'maps' && incubatorMapCount === 0 && onGenerateMaps) {
      const { incubatorCounts, preferenceOrder } =
        getStoredIncubatorSelection();
      onGenerateMaps(incubatorCounts, preferenceOrder);
      onTabChange('maps');
      return;
    }
    onTabChange(tab);
  };

  const tabs: WorkflowTab<FertWorkTab>[] = [
    {
      key: 'session',
      label: t('fertWork.tabs.session', 'Session'),
      icon: 'pi pi-clipboard',
    },
    {
      key: 'runs',
      label: t('fertWork.tabs.runs', 'Runs'),
      icon: 'pi pi-objects-column',
      badge: getBadge('runs'),
      loading: isGeneratingRuns,
      dropdownContent: showRunsDropdown ? (
        <RunsDropdown onSelect={handleRunsSelect} />
      ) : undefined,
    },
    {
      key: 'labels',
      label: t('fertWork.tabs.labels', 'Labels'),
      icon: 'pi pi-tags',
      badge: getBadge('labels'),
      locked: isTabLocked('labels'),
    },
    {
      key: 'maps',
      label: t('fertWork.tabs.maps', 'Maps'),
      icon: 'pi pi-th-large',
      badge: getBadge('maps'),
      loading: isGeneratingMaps,
      locked: isTabLocked('maps'),
      dropdownContent: showMapsDropdown ? (
        <MapsDropdown
          onGenerate={handleMapsGenerate}
          labelCount={labelCount}
          sessionId={sessionId}
        />
      ) : undefined,
    },
    {
      key: 'sof-labels',
      label: t('fertWork.tabs.ivc', 'IVC'),
      icon: 'pi pi-tag',
      badge: getBadge('sof-labels'),
      locked: isTabLocked('sof-labels'),
    },
  ];

  return (
    <WorkflowTabBar<FertWorkTab>
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={handleTabChange}
    />
  );
};
